'use client';

// ═══════════════════════════════════════════════════════════════
// Currency Selector — Clean dropdown for storefront navbar
// Professional design without flags, matching the icon bar style
// ═══════════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useCurrency, CURRENCY_NAMES_AR } from '@/themes/core/hooks/useCurrency';

interface CurrencySelectorProps {
    /** Visual variant for different navbar styles */
    variant?: 'default' | 'dark' | 'transparent';
    /** Additional className */
    className?: string;
}

export function CurrencySelector({
    variant = 'default',
    className = '',
}: CurrencySelectorProps) {
    const {
        selectedCurrency,
        enabledCurrencies,
        showSelector,
        setSelectedCurrency,
        baseCurrency,
    } = useCurrency();

    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close on click outside
    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        }
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => { document.removeEventListener('mousedown', handleClickOutside); };
        }
        return undefined;
    }, [isOpen]);

    // Close on Escape
    useEffect(() => {
        function handleEsc(e: KeyboardEvent) {
            if (e.key === 'Escape') setIsOpen(false);
        }
        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
            return () => { document.removeEventListener('keydown', handleEsc); };
        }
        return undefined;
    }, [isOpen]);

    // Don't render if only 1 currency
    if (!showSelector) return null;

    // Variant-based styling
    const buttonStyles = {
        default: 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:text-white dark:hover:bg-neutral-800',
        dark: 'text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10',
        transparent: 'text-white/80 hover:text-white hover:bg-white/10',
    };

    return (
        <div ref={dropdownRef} className={`relative ${className}`}>
            {/* Trigger — clean text button matching icon bar */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`
                    relative inline-flex items-center gap-1 rounded-lg px-2 py-2 transition-all duration-200
                    text-xs font-semibold tracking-wide
                    ${buttonStyles[variant]}
                `}
                aria-label="تغيير العملة"
                aria-expanded={isOpen}
            >
                <span>{selectedCurrency}</span>
                <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div
                    className={`
                        absolute top-full mt-1.5 z-[100]
                        min-w-[160px] overflow-hidden
                        rounded-lg border border-neutral-200 bg-white
                        shadow-lg shadow-black/5
                        dark:bg-neutral-900 dark:border-neutral-700
                        right-0
                    `}
                    role="listbox"
                    aria-label="العملات المتاحة"
                >
                    {enabledCurrencies.map((code) => {
                        const isActive = code === selectedCurrency;
                        const isBase = code === baseCurrency;

                        return (
                            <button
                                key={code}
                                role="option"
                                aria-selected={isActive}
                                onClick={() => {
                                    setSelectedCurrency(code);
                                    setIsOpen(false);
                                }}
                                className={`
                                    w-full flex items-center justify-between px-3 py-2.5
                                    text-sm transition-colors duration-100 cursor-pointer
                                    ${isActive
                                        ? 'bg-neutral-100 dark:bg-neutral-800 font-semibold'
                                        : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50'
                                    }
                                `}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-xs w-8">{code}</span>
                                    <span className="text-neutral-500 dark:text-neutral-400 text-xs truncate">
                                        {CURRENCY_NAMES_AR[code] || code}
                                    </span>
                                </div>

                                <div className="flex items-center gap-1.5">
                                    {isBase && (
                                        <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400">
                                            أساسية
                                        </span>
                                    )}
                                    {isActive && (
                                        <svg className="h-3.5 w-3.5 text-neutral-900 dark:text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                        </svg>
                                    )}
                                </div>
                            </button>
                        );
                    })}

                    {/* Approximate notice */}
                    <div className="px-3 py-1.5 border-t border-neutral-100 dark:border-neutral-700">
                        <p className="text-[10px] text-neutral-400 text-center">
                            الأسعار المحوّلة تقريبية
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CurrencySelector;
